import { useEffect, useState } from 'react';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Users, Truck, UserCircle, Tag, Link2, ArrowLeftRight, Repeat,
  Clock, Loader2, AlertTriangle, ShieldCheck,
} from 'lucide-react';
import { cur } from '@/lib/currency';
import api from '@/lib/api';

function fmt(v) { return (Number(v) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }

/**
 * TxnActionDrawer — single-transaction review panel for the Banking
 * Reconciliation Inbox. It REUSES existing validated workflows only:
 *   • Add Payment / Add Expense  -> parent opens the existing entity link
 *     dialog (which posts to /banking/statement-txn/bulk-link-to-entity).
 *   • Link Existing / Transfer / Pass-Through -> deep-link to the existing
 *     validated page for this txn (not rebuilt).
 *   • Review Later -> parent hides the row locally (non-persistent).
 *   • Duplicate check -> read-only GET /banking/quick-link/duplicate-candidates.
 * No new engine, no parallel payment workflow.
 */
export function TxnActionDrawer({
  txn, open, onClose,
  onAddPayment, onAddExpense, onLinkExisting, onTransfer, onPassThrough, onReviewLater,
}) {
  const [dups, setDups] = useState(null);     // null = not checked yet
  const [dupLoading, setDupLoading] = useState(false);

  useEffect(() => {
    setDups(null);
    if (!open || !txn) return;
    // Auto-run the read-only duplicate scan so "Possible Duplicate" is
    // surfaced inline without a separate double-counting tab.
    (async () => {
      setDupLoading(true);
      try {
        const r = await api.get('/banking/quick-link/duplicate-candidates', {
          params: {
            row_type: 'statement_txn',
            // row_id is required by the route signature (unused for
            // statement_txn, which keys off statement_id + txn_index).
            row_id: `${txn.statement_id}::${txn.txn_index}`,
            statement_id: txn.statement_id,
            txn_index: txn.txn_index,
          },
        });
        setDups(r.data?.candidates || []);
      } catch { setDups([]); }
      setDupLoading(false);
    })();
  }, [open, txn]);

  if (!txn) return null;
  const incoming = (txn.amount || 0) >= 0;
  const hasDups = Array.isArray(dups) && dups.length > 0;
  const feeKnown = txn.transfer_fee !== null && txn.transfer_fee !== undefined;
  const vatKnown = txn.transfer_vat !== null && txn.transfer_vat !== undefined;

  const row = (label, value) => (
    <div className="flex items-center justify-between gap-3 py-1.5 border-b border-border/40 last:border-0">
      <span className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</span>
      <span className="text-xs font-medium text-foreground text-right truncate max-w-[60%]">{value}</span>
    </div>
  );

  return (
    <Sheet open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="txn-action-drawer">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            Review transaction
            <Badge variant="outline" className={incoming ? 'text-success border-success/30 bg-success/10' : 'text-error border-error/30 bg-error/10'}>
              {incoming ? 'Incoming ↓' : 'Outgoing ↑'}
            </Badge>
          </SheetTitle>
          <SheetDescription className="text-left">
            Pick the one action that matches this bank line. Posting actions ask you to
            choose the counterparty before anything is saved.
          </SheetDescription>
        </SheetHeader>

        {/* Amount */}
        <div className="mt-4 rounded-xl bg-muted/40 border border-border p-4 text-center">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Bank amount</p>
          <p className={`text-2xl font-bold mt-0.5 ${incoming ? 'text-success' : 'text-error'}`} data-testid="drawer-amount">
            {cur()} {fmt(Math.abs(txn.amount))}
          </p>
        </div>

        {/* Verified real details — show "Unassigned" / N/A for anything
            genuinely missing; never fabricate a branch or a zero. */}
        <div className="mt-3 rounded-xl border border-border p-3">
          {row('Date', txn.date || '—')}
          {row('Bank', txn.bank_name || '—')}
          {row('Reference', txn.reference || '—')}
          {row('Branch', txn.branch_id
            ? (txn.branch_name || <span className="italic text-muted-foreground">Unknown branch</span>)
            : <span className="italic text-muted-foreground">Unassigned / Unknown</span>)}
          {row('Description', txn.description || '—')}
        </div>

        {/* Financial breakdown — reuses existing bank-fee fields. Values that
            don't exist for an unmatched txn are clearly marked, never zero. */}
        <div className="mt-3 rounded-xl border border-border p-3" data-testid="drawer-financials">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">Amounts & differences</p>
          {row('Bank amount', `${cur()} ${fmt(Math.abs(txn.amount))}`)}
          {row('Bank fee', feeKnown ? `${cur()} ${fmt(txn.transfer_fee)}` : <span className="italic text-muted-foreground">Not available</span>)}
          {row('VAT on fee', vatKnown ? `${cur()} ${fmt(txn.transfer_vat)}` : <span className="italic text-muted-foreground">Not available</span>)}
          {row('Expected (book) amount', <span className="italic text-muted-foreground">N/A until linked</span>)}
          {row('Outstanding difference', <span className="italic text-muted-foreground">N/A until linked</span>)}
        </div>

        {/* Possible-duplicate signal (read-only) */}
        <div className="mt-3" data-testid="drawer-dup-check">
          {dupLoading ? (
            <p className="text-xs text-muted-foreground flex items-center gap-1.5"><Loader2 size={12} className="animate-spin" /> Checking for possible duplicates…</p>
          ) : hasDups ? (
            <div className="rounded-lg border border-warning/40 bg-warning/10 p-2.5" data-testid="drawer-dup-warning">
              <p className="text-xs font-semibold text-warning flex items-center gap-1.5"><AlertTriangle size={13} /> {dups.length} possible duplicate(s) already recorded</p>
              <ul className="mt-1.5 space-y-1">
                {dups.slice(0, 4).map((d, i) => (
                  <li key={i} className="text-[11px] text-muted-foreground flex items-center justify-between gap-2">
                    <span className="truncate">{d.collection} · {d.label || d.name || d.party_name || d.id?.slice(0, 8)}</span>
                    <span className="font-mono">{cur()} {fmt(d.amount)}</span>
                  </li>
                ))}
              </ul>
              <p className="text-[10px] text-muted-foreground mt-1.5">Use <strong>Link Existing</strong> to connect to one of these instead of creating a new record.</p>
            </div>
          ) : dups !== null ? (
            <p className="text-xs text-success flex items-center gap-1.5" data-testid="drawer-dup-clear"><ShieldCheck size={13} /> No likely duplicates found.</p>
          ) : null}
        </div>

        {/* Actions — only relevant ones; posting actions open the entity dialog */}
        <div className="mt-4 space-y-2">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Take action</p>
          <Button variant="outline" className="w-full justify-start h-10" onClick={() => onLinkExisting(txn)} data-testid="drawer-link-existing">
            <Link2 size={15} className="mr-2 text-primary" /> Link to an existing record
            <span className="ml-auto text-[10px] text-muted-foreground">opens Quick-Link</span>
          </Button>
          <Button variant="outline" className="w-full justify-start h-10" onClick={() => onAddPayment(txn)} data-testid="drawer-add-payment">
            <Users size={15} className="mr-2 text-info" /> Add payment to a party
            <span className="ml-auto text-[10px] text-muted-foreground">supplier · employee · partner · customer</span>
          </Button>
          <Button variant="outline" className="w-full justify-start h-10" onClick={() => onAddExpense(txn)} data-testid="drawer-add-expense">
            <Tag size={15} className="mr-2 text-warning" /> Record as an expense
          </Button>
          <Button variant="outline" className="w-full justify-start h-10" onClick={() => onTransfer(txn)} data-testid="drawer-transfer">
            <ArrowLeftRight size={15} className="mr-2 text-violet-600" /> Bank transfer
            <span className="ml-auto text-[10px] text-muted-foreground">opens Banking Hub</span>
          </Button>
          <Button variant="outline" className="w-full justify-start h-10" onClick={() => onPassThrough(txn)} data-testid="drawer-passthrough">
            <Repeat size={15} className="mr-2 text-teal-600" /> Pass-through advance
            <span className="ml-auto text-[10px] text-muted-foreground">opens Banking Hub</span>
          </Button>
          <Button variant="ghost" className="w-full justify-start h-10 text-muted-foreground" onClick={() => onReviewLater(txn)} data-testid="drawer-review-later">
            <Clock size={15} className="mr-2" /> Review later
            <span className="ml-auto text-[10px] text-muted-foreground">hides this session only</span>
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
